import { isImmutable } from "immutable";
import { isFunction } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ReactTags from "react-tag-autocomplete";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormColorDropdown from "../../components/form/FormColorDropdown";
import FormDropdown from "../../components/form/FormDropdown";
import FormTextField from "../../components/form/FormTextField";
import Icon from "../../components/interface/Icon";
import clipboardCatcherClassName from "../../constants/clipboardCatcherClassName";
import swatchColorOptions from "../../constants/colorOptions";
import { COLUMN_ID_TO_DATE_FIELD } from "../../constants/dateFields";
import KeyConstants from "../../constants/keyConstants";
import outlineColumnIds, { OUTLINE_COLUMN_BULK_CHANGE_DATES } from "../../constants/outlineColumnIds";
import PubSubTopics from "../../constants/pubSubTopics";
import tableCellTypes from "../../constants/tableCellTypes";
import { TAGS_INPUT_PLACEHOLDER } from "../../constants/tags";
import { PropTypeShapes } from "../../types";
import { getModifierKeysForEvent, hasMultipleModifiers } from "../../utils/getModifierKeys";
import userInputValidators from "../../utils/userInputValidators";
import FormDatePicker from "../form/formDatePicker/FormDatePicker";
import FormDurationPicker from "../form/FormDurationPicker";
import FormIdentifierList from "../form/FormIdentifierList";
import Identifier from "../interface/Identifier";

// import debugUtils from "../../utils/debugUtils";

const cellTypeIsDropdown = cellType =>
  cellType === tableCellTypes.colorDropdown ||
  cellType === tableCellTypes.dropdown ||
  cellType === tableCellTypes.singleReference ||
  cellType === tableCellTypes.multipleReference ||
  cellType === tableCellTypes.list ||
  cellType === tableCellTypes.avatarList;

const isTagInputAvailable = (cellType, cellEditor) => {
  return cellType === tableCellTypes.tag && cellEditor.input && cellEditor.input.input;
};

class TableCellView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { editing: false };
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onCut = this.onCut.bind(this);
    this.onCopy = this.onCopy.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.onPopoverToggle = this.onPopoverToggle.bind(this);
    this.clearCellIfFocused = this.clearCellIfFocused.bind(this);
    this.clearCell = this.clearCell.bind(this);
    this.shouldFocusClipboardCatcher = this.shouldFocusClipboardCatcher.bind(this);
    this.focusClipboardCatcher = this.focusClipboardCatcher.bind(this);
    this.focusIsCell = this.focusIsCell.bind(this);
    this.focusIsCellEditor = this.focusIsCellEditor.bind(this);
    this.focusIsNotEditor = this.focusIsNotEditor.bind(this);
    this.isCellClipboardAction = this.isCellClipboardAction.bind(this);

    this.getOptions = this.getOptions.bind(this);

    this.refreshFocus = this.refreshFocus.bind(this);

    this.onClipboardEventPendingMessageReceived = this.onClipboardEventPendingMessageReceived.bind(this);
    this.removeClipboardFocusIfNecessary = this.removeClipboardFocusIfNecessary.bind(this);

    this._cellEditor = null;
    this._cellPopoverOpen = false;
    this._clipboardCaptureNode = null;
    this._domNode = null;
  }

  componentDidMount() {
    this._pubSubClipboardFocusToken = PubSub.subscribe(
      PubSubTopics.clipboardEventPending,
      this.onClipboardEventPendingMessageReceived
    );
    this.props.registerCellFocusCallback(this.props.rowId, this.props.columnId, this.refreshFocus);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges(`TableCellView [${nextProps.row}, ${nextProps.columnId}]`, this.props, nextProps, true);
  // }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubClipboardFocusToken);
    this.props.unregisterCellFocusCallback(this.props.rowId, this.props.columnId, this.refreshFocus);
  }

  refreshFocus() {
    if (!this._cellPopoverOpen && this.props.focused) {
      if (!this.focusIsCell()) {
        this.focusDomNode();
      }
    }
  }

  focusClipboardCatcher() {
    if (this._clipboardCaptureNode) {
      this._clipboardCaptureNode.value = " ";
      this._clipboardCaptureNode.focus();
      this._clipboardCaptureNode.setSelectionRange(0, 1);
    }
  }

  focusDomNode() {
    this._domNode.focus();
  }

  focusIsCell() {
    return document.activeElement === this._domNode || this._domNode.contains(document.activeElement);
  }

  focusIsCellEditor() {
    return this.focusIsCell() && !this.focusIsNotEditor();
  }

  focusIsNotEditor() {
    return document.activeElement === this._domNode || document.activeElement === this._clipboardCaptureNode;
  }

  shouldFocusClipboardCatcher() {
    return document.activeElement === this._domNode || this.props.cellType === tableCellTypes.checkbox;
  }

  onKeyDown(e) {
    if (hasMultipleModifiers(e)) {
      return;
    }

    // If the popover is open, then we don't want to listen to or respond to any
    // keyboard commands ourselves
    if (this._cellPopoverOpen) {
      return;
    }

    let keyHandled = false;

    // We want to use keyDown events to move between cells
    switch (e.key) {
      case KeyConstants.Tab:
      case KeyConstants.Up:
      case KeyConstants.Down:
      case KeyConstants.Left:
      case KeyConstants.Right:
        if (this.props.cellType !== tableCellTypes.tag || this.focusIsNotEditor()) {
          this.props.moveFocus(e);
          keyHandled = true;
        }
        break;
      case KeyConstants.Enter:
        if (this.props.disabled) {
          break;
        }
        if (cellTypeIsDropdown(this.props.cellType)) {
          this.startEditing();
        } else if (!isTagInputAvailable(this.props.cellType, this._cellEditor) || !this._cellEditor.input.input.value) {
          this.props.moveFocus(e);
        }
        keyHandled = true;
        break;
      case KeyConstants.Escape:
        this.stopEditing();
        keyHandled = true;
        break;
      case KeyConstants.Delete:
      case KeyConstants.Backspace: {
        if (this.props.disabled) {
          break;
        }
        if (this.focusIsNotEditor()) {
          this.props.onCellDelete();
          keyHandled = true;
        }
        break;
      }
    }

    if (keyHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  onKeyPress(e) {
    // If the user starts typing in the cell, focus the inner editor.
    // This first character will actually then be received by the child also.
    // Note: onKeyPress seems to only fire on printable characters and not on keys
    // such as Tab, Backspace, etc. so this should be safe.
    if (!this._cellPopoverOpen && this.focusIsNotEditor()) {
      // Note that for input texts and the Enter key, we do this onKeyUp instead of onKeyPress so that
      // we don't immediately erase the text and replace with an enter character.
      // However, we do want the control to get focus if it is a dropdown cell and they hit enter
      if (e.key !== KeyConstants.Enter && !e.ctrlKey && !e.metaKey) {
        this.startEditing();
      }
    }
  }

  onKeyUp(_e) {
    // TODO: Do we want ENTER to begin editing the cell, or to navigate to the next cell? What do spreadsheet apps do?
    // For the enter key only, we want to focus on key up instead.
    // This is to stop the enter character from deleting all of the selected text in the cell.
    // if (this.focusIsNotEditor()) {
    //   if (e.key === KeyConstants.Enter) {
    //     this.startEditing();
    //   }
    // }
  }

  isCellClipboardAction() {
    return cellTypeIsDropdown(this.props.cellType) || this.focusIsNotEditor();
  }

  onClipboardEventPendingMessageReceived(_msg) {
    if (this.shouldFocusClipboardCatcher()) {
      this._defocusClipboardCatcherAfterEvent = true;
      this.focusClipboardCatcher();
    }
  }

  removeClipboardFocusIfNecessary() {
    // If we want to automatically remove focus after clipboard event, and we are focused on
    // the clipboard catcher, return focus back to the cell itself...
    if (this._defocusClipboardCatcherAfterEvent && this.focusIsCell() && this.focusIsNotEditor()) {
      this.focusDomNode();
    }
  }

  onCut(e) {
    if (this.isCellClipboardAction()) {
      this.props.onCellCut(e);
    }
    this.removeClipboardFocusIfNecessary();
  }

  onCopy(e) {
    if (this.isCellClipboardAction()) {
      this.props.onCellCopy(e);
    }
    this.removeClipboardFocusIfNecessary();
  }

  onPaste(e) {
    if (this.isCellClipboardAction()) {
      this.props.onCellPaste(e);
    }
    this.removeClipboardFocusIfNecessary();
  }

  startEditing() {
    if (this._cellEditor) {
      if (this._cellEditor.focus) {
        this._cellEditor.focus();
      }
      if (cellTypeIsDropdown(this.props.cellType) && this._cellEditor) {
        this._cellEditor.showMenu();
      } else if (isTagInputAvailable(this.props.cellType, this._cellEditor)) {
        this._cellEditor.input.input.focus();
      } else if (this.props.cellType === tableCellTypes.checkbox && this._cellEditor) {
        this._cellEditor.toggle();
      }
    }
  }

  stopEditing() {
    this.focusDomNode();
  }

  clearCellIfFocused() {
    if (document.activeElement === this._clipboardCaptureNode) {
      this.clearCell();
    }
  }

  clearCell() {
    this.props.onValueChanged(this.props.row, this.props.columnId, "");
  }

  onPopoverToggle(value) {
    // Record popover state
    this._cellPopoverOpen = value;
    // And tell the table view the same
    this.props.onPopoverToggle(value);
  }

  getOptions() {
    return isFunction(this.props.options)
      ? this.props.options(this.props.rowId, this.props.columnId)
      : isImmutable(this.props.options)
        ? this.props.options.toJS()
        : this.props.options;
  }

  renderControl() {
    switch (this.props.cellType) {
      case tableCellTypes.colorDropdown: {
        return (
          <FormColorDropdown
            ref={node => (this._cellEditor = node)}
            options={this.getOptions}
            moreColors={swatchColorOptions}
            value={this.props.value}
            openOnFocus={false}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
            onToggleMenu={this.onPopoverToggle}
          />
        );
      }
      case tableCellTypes.dropdown: {
        return (
          <FormDropdown
            ref={node => (this._cellEditor = node)}
            options={this.getOptions}
            value={this.props.value}
            openOnFocus={false}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
            onToggleMenu={this.onPopoverToggle}
            disableTabLock={true}
          />
        );
      }
      case tableCellTypes.text:
        return (
          <FormTextField
            ref={node => (this._cellEditor = node)}
            value={this.props.value}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            noBlurOnCommit={true}
            onChange={value => {
              this.props.onValueChanged(this.props.row, this.props.columnId, value);
            }}
            isStruckOut={this.props.isStruckOut}
          />
        );
      case tableCellTypes.wrappedtext:
        return (
          <FormTextField
            ref={node => (this._cellEditor = node)}
            value={this.props.value}
            multiline={true}
            allowNewLine={false}
            noBlurOnCommit={true}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
            isStruckOut={this.props.isStruckOut}
          />
        );
      case tableCellTypes.multitext:
        return (
          <FormTextField
            ref={node => (this._cellEditor = node)}
            value={this.props.value}
            multiline={true}
            allowNewLine={true}
            mustEscapeNewLine={true}
            noBlurOnCommit={true}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
            isStruckOut={this.props.isStruckOut}
          />
        );
      case tableCellTypes.number:
        return (
          <FormTextField
            ref={node => (this._cellEditor = node)}
            value={this.props.value}
            noBlurOnCommit={true}
            numeric={true}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
          />
        );
      case tableCellTypes.integer:
        return (
          <FormTextField
            innerRef={node => (this._cellEditor = node)}
            value={this.props.value}
            noBlurOnCommit={true}
            numeric={true}
            isInteger={true}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
          />
        );
      case tableCellTypes.checkbox:
        return (
          <FormCheckbox
            ref={node => (this._cellEditor = node)}
            checkboxState={this.props.value}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
          />
        );
      case tableCellTypes.singleReference:
        return (
          <FormDropdown
            ref={node => (this._cellEditor = node)}
            options={this.getOptions}
            value={this.props.value}
            openOnFocus={true}
            disabled={this.props.disabled}
            onChange={value => this.props.onMultiValueChanged(this.props.row, this.props.columnId, value, true)}
            onToggleMenu={this.onPopoverToggle}
            disableTabLock={true}
          />
        );
      case tableCellTypes.multipleReference:
        return (
          <FormIdentifierList
            ref={node => (this._cellEditor = node)}
            options={this.getOptions}
            values={this.props.value}
            openOnFocus={true}
            allowSearch={true}
            disabled={this.props.disabled}
            onChange={(value, newState) =>
              this.props.onMultiValueChanged(this.props.row, this.props.columnId, value, newState)
            }
            onToggleMenu={this.onPopoverToggle}
            disableTabLock={true}
            remaining={this.props.remaining}
            isTableCell={true}
          />
        );
      case tableCellTypes.date: {
        const {
          columnId,
          startDate,
          latestStartDate,
          earliestEndDate,
          endDate,
          duration,
          durationToEarliestEnd,
          autoAdjustEndDate,
          calendar,
          disabled,
          ongoing,
          startLabel,
          endLabel
        } = this.props;
        const isDual = ongoing ? false : true;
        const selectedDateField = COLUMN_ID_TO_DATE_FIELD[columnId];
        const getOnDateChange = columnId => value => this.props.onValueChanged(this.props.row, columnId, value);
        return (
          <FormDatePicker
            isDual={isDual}
            allowUncertainDates={true}
            ref={node => (this._cellEditor = node)}
            selectedDateField={selectedDateField}
            startDate={startDate}
            latestStartDate={latestStartDate}
            earliestEndDate={earliestEndDate}
            endDate={endDate}
            durationToEarliestEnd={durationToEarliestEnd}
            duration={duration}
            autoAdjustEndDate={autoAdjustEndDate}
            calendar={calendar}
            disabled={disabled}
            ongoing={ongoing}
            onStartDateChange={getOnDateChange(outlineColumnIds.start)}
            onLatestStartChange={getOnDateChange(outlineColumnIds.latestStart)}
            onEarliestEndChange={getOnDateChange(outlineColumnIds.earliestEnd)}
            onEndDateChange={getOnDateChange(outlineColumnIds.end)}
            onBulkDateChange={getOnDateChange(OUTLINE_COLUMN_BULK_CHANGE_DATES)}
            onToggleCalendarPicker={this.onPopoverToggle}
            startLabel={startLabel}
            endLabel={endLabel}
            noBlurOnCommit={true}
          />
        );
      }
      case tableCellTypes.duration:
        return (
          <FormDurationPicker
            ref={node => (this._cellEditor = node)}
            value={this.props.value}
            calendar={this.props.calendar}
            disabled={this.props.disabled}
            onChange={value => this.props.onValueChanged(this.props.row, this.props.columnId, value)}
            validator={userInputValidators.itemDuration}
            noBlurOnCommit={true}
          />
        );
      case tableCellTypes.label:
        return <span className="cellLabel">{this.props.value}</span>;
      case tableCellTypes.identifier:
        return (
          <Identifier
            isAvatar={this.props.isAvatar}
            image={this.props.image}
            label={this.props.value}
            color={this.props.color}
          />
        );
      case tableCellTypes.tag:
        return (
          <ReactTags
            readOnly={this.props.disabled}
            ref={node => (this._cellEditor = node)}
            placeholder={TAGS_INPUT_PLACEHOLDER}
            tags={this.props.value || []}
            suggestions={this.getOptions()}
            handleAddition={newTag =>
              this.props.onValueChanged(this.props.row, this.props.columnId, {
                tagNames: [newTag.name],
                isAdding: true
              })
            }
            handleDelete={tagIndex => {
              this.props.onValueChanged(this.props.row, this.props.columnId, {
                tagNames: [this.props.value[tagIndex].name],
                isAdding: false
              });
            }}
            addOnBlur={true}
            autofocus={false}
            allowNew={true}
          />
        );
      default:
        return <span className="cellLabel" />;
    }
  }

  render() {
    const cellStyle = {
      width: this.props.width + "px",
      maxWidth: this.props.width + "px",
      flex: `0 0 ${this.props.width}px`
    };

    const onClick = e =>
      this.props.onClick(this.props.row, this.props.column, getModifierKeysForEvent(e), this.props.rowId);
    const onDoubleClick = e => this.props.onDoubleClick(this.props.row, this.props.column, getModifierKeysForEvent(e));

    let classNames = ["tableCell", `indent${this.props.indentationLevel > 6 ? "Deep" : this.props.indentationLevel}`];
    if (this.props.selected) {
      classNames.push("selected");
    }
    if (this.props.disabled) {
      classNames.push("disabled");
    }
    if (!this.props.singleSelection) {
      classNames.push("multiple");
    }
    if (!this.props.singleGroup) {
      classNames.push("multipleGroup");
    }

    this._clipboardCaptureNode = null;
    this._domNode = null;

    const { isWarning, warningMessage } = this.props;

    return (
      <div
        ref={node => (this._domNode = node)}
        id={this.props.cellId}
        className={classNames.join(" ")}
        style={cellStyle}
        tabIndex={0}
        onKeyDown={this.onKeyDown}
        onKeyUp={this.onKeyUp}
        onKeyPress={this.onKeyPress}
        onCut={this.onCut}
        onCopy={this.onCopy}
        onPaste={this.onPaste}
        title={isWarning ? warningMessage : ""}
      >
        {this.props.icon ? (
          <Icon
            className="cellIcon"
            icon={this.props.icon}
            color={this.props.iconColor ? this.props.iconColor : "iconMono"}
          />
        ) : null}
        {this.props.hasChildren ? (
          <Icon
            className="childArrow"
            icon={this.props.childrenExpanded ? "downArrow" : "rightArrow"}
            color="iconMono"
            onClick={() =>
              this.props.onToggleChildren
                ? this.props.onToggleChildren(this.props.row, !this.props.childrenExpanded)
                : null
            }
          />
        ) : null}
        <div className={`tableCellEditor${isWarning ? " isWarning" : ""}`}>
          {this.renderControl()}
          {this.props.focused ? (
            <input
              type="text"
              defaultValue=" "
              className={`tableCellClipboardCatcher ${clipboardCatcherClassName}`}
              ref={node => (this._clipboardCaptureNode = node)}
            />
          ) : (
            <div className="tableCellClickCatcher" onClick={onClick} onDoubleClick={onDoubleClick} />
          )}
        </div>
      </div>
    );
  }
}

TableCellView.propTypes = {
  // Cell Data
  cellId: PropTypes.string.isRequired,
  row: PropTypes.number.isRequired,
  rowId: PropTypes.string.isRequired,
  column: PropTypes.number.isRequired,
  columnId: PropTypes.string.isRequired,
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  remaining: PropTypes.number,
  ongoing: PropTypes.bool,
  startDate: PropTypeShapes.dateRelated,
  latestStartDate: PropTypeShapes.dateRelated,
  earliestEndDate: PropTypeShapes.dateRelated,
  endDate: PropTypeShapes.dateRelated,
  duration: PropTypeShapes.timespan,
  durationToEarliestEnd: PropTypeShapes.timespan,
  autoAdjustEndDate: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isWarning: PropTypes.bool,
  warningMessage: PropTypes.string,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  isStruckOut: PropTypes.bool,

  selected: PropTypes.bool.isRequired,
  focused: PropTypes.bool.isRequired,

  // Registering cells with TableView
  registerCellFocusCallback: PropTypes.func.isRequired,
  unregisterCellFocusCallback: PropTypes.func.isRequired,

  // Column Info
  cellType: PropTypes.string.isRequired,
  options: PropTypes.any, // eslint-disable-line react/forbid-prop-types

  width: PropTypes.number.isRequired,

  // Used for DatePicker
  calendar: PropTypeShapes.calendar,

  // Used for dependencies
  // getCellOptions: PropTypes.func,

  // TODO: How do we show identifier again...?
  // identifier: PropTypeShapes.identifier,
  hasChildren: PropTypes.bool,
  childrenExpanded: PropTypes.bool,
  indentationLevel: PropTypes.number,
  onToggleChildren: PropTypes.func,

  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,
  moveFocus: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onPopoverToggle: PropTypes.func.isRequired,
  onCellCut: PropTypes.func.isRequired,
  onCellCopy: PropTypes.func.isRequired,
  onCellPaste: PropTypes.func.isRequired,
  onCellDelete: PropTypes.func.isRequired,

  // Whether a single cell or multiple cells are selected, for styling
  singleSelection: PropTypes.bool,
  // Whether a single group or multiple groups are selected, for styling
  singleGroup: PropTypes.bool,

  isAvatar: PropTypes.bool,
  image: PropTypes.string,
  color: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string
};

export default TableCellView;
